if (!customElements.get('product-reels')) {
  customElements.define(
    'product-reels',
    class ProductReels extends HTMLElement {
      connectedCallback() {
        this.cards = Array.from(this.querySelectorAll('[data-reel-card]'));
        this.autoplay = this.closest('[data-autoplay]')?.dataset.autoplay === 'true';
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.initObservers = this.initObservers.bind(this);
        this.scheduleObservers = this.scheduleObservers.bind(this);

        this.cards.forEach((card) => {
          const video = card.querySelector('video');
          const toggle = card.querySelector('[data-reel-toggle]');
          if (!video || !toggle) return;

          toggle.addEventListener('click', () => this.toggleVideo(card));
          video.addEventListener('play', () => this.setPlayingState(card, true));
          video.addEventListener('pause', () => this.setPlayingState(card, false));
        });

        if (document.readyState === 'complete') {
          this.scheduleObservers();
        } else {
          window.addEventListener('load', this.scheduleObservers, { once: true });
        }
      }

      scheduleObservers() {
        ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((eventName) => {
          window.addEventListener(eventName, this.initObservers, { once: true, passive: true });
        });
      }

      initObservers() {
        if (this.observersReady) return;

        this.observersReady = true;
        ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((eventName) => {
          window.removeEventListener(eventName, this.initObservers);
        });

        if ('IntersectionObserver' in window) {
          this.loadObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const rect = entry.target.getBoundingClientRect();
                const verticallyNear = rect.top <= window.innerHeight + 600 && rect.bottom >= -600;
                const horizontallyNear = rect.left <= window.innerWidth + 200 && rect.right >= -200;
                if (!entry.isIntersecting || !verticallyNear || !horizontallyNear) return;

                this.loadVideo(entry.target.querySelector('video'));
                this.loadObserver.unobserve(entry.target);
              });
            },
            { rootMargin: '600px 200px', threshold: 0 }
          );

          this.cards.forEach((card) => this.loadObserver.observe(card));
        }

        if (!this.autoplay || this.reduceMotion || !('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target.querySelector('video');
              if (!video) return;

              const rect = entry.target.getBoundingClientRect();
              const visiblyOnScreen =
                rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;

              if (entry.isIntersecting && entry.intersectionRatio >= 0.65 && visiblyOnScreen) {
                this.pauseOtherVideos(entry.target);
                this.loadVideo(video);
                video.play().catch(() => {});
              } else {
                video.pause();
              }
            });
          },
          { threshold: [0, 0.65, 1] }
        );

        this.cards.forEach((card) => this.observer.observe(card));
      }

      disconnectedCallback() {
        window.removeEventListener('load', this.scheduleObservers);
        ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((eventName) => {
          window.removeEventListener(eventName, this.initObservers);
        });
        this.observer?.disconnect();
        this.loadObserver?.disconnect();
      }

      toggleVideo(card) {
        const video = card.querySelector('video');
        if (!video) return;

        if (video.paused) {
          this.pauseOtherVideos(card);
          this.loadVideo(video);
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }

      pauseOtherVideos(activeCard) {
        this.cards.forEach((card) => {
          if (card !== activeCard) card.querySelector('video')?.pause();
        });
      }

      loadVideo(video) {
        if (!video) return;

        let sourceAdded = false;
        video.querySelectorAll('source[data-reel-src]').forEach((source) => {
          source.src = source.dataset.reelSrc;
          source.removeAttribute('data-reel-src');
          sourceAdded = true;
        });

        if (sourceAdded) video.load();
      }

      setPlayingState(card, playing) {
        const toggle = card.querySelector('[data-reel-toggle]');
        const caption = card.querySelector('.product-reels__caption')?.textContent?.trim() || 'reel';

        card.classList.toggle('product-reels__card--playing', playing);
        if (toggle) toggle.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${caption}`);
      }
    }
  );
}
